import { newsData, workData } from './pageLandingData';

import AboutMeCard from '../components/AboutMeCard';
import { Anchor } from '../components/Anchor';
import Page from '../design-system/Page';
import PhotoCard from '../components/PhotoCard';
import styled from 'styled-components';

const AboutMeSection = styled.section`
  width: min(1180px, 100%);
  display: flex;
  flex-direction: row;
  gap: 48px;
  justify-content: center;
  margin-top: 10%;
  align-items: center;
  @media (max-width: 1180px) {
    flex-direction: column;
    align-items: center;
  }
  margin-right: auto;
`;

const WorkSection = styled.section`
  width: min(1040px, 100%);
  display: flex;
  flex-direction: column;
  gap: 22px;
  margin: 48px auto 80px;
  @media (max-width: 1024px) {
    margin-top: 40px;
    margin-bottom: 64px;
  }
`;

export const HighlightSpan = styled.span`
  background: linear-gradient(to right, rgba(255, 255, 255, 0) 50%, rgba(247, 209, 200, 0.5) 50%);
  background-size: 200%;
  background-position: 0 0;
  display: inline;
  transition: 0.5s ease;
  &:hover {
    background-position: -100% 0;
    text-decoration: none;
    color: #064554;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #2e3442;
  font-size: clamp(24px, 3vw, 32px);
  line-height: 1.15;
`;

const Timeline = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const TimelineItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 0 16px;
  border-top: 1px solid rgba(59, 69, 90, 0.14);
`;

const TimelineHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
`;

const TimelineTitle = styled.h3`
  margin: 0;
  color: #232936;
  font-size: clamp(15px, 1.8vw, 18px);
  line-height: 1.3;
`;

const TimelineMeta = styled.p`
  margin: 0;
  color: #5f6776;
  font-size: 13px;
  line-height: 1.45;
`;

const TimelineRole = styled.span`
  color: #8b796d;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const TimelineYear = styled.span`
  flex-shrink: 0;
  color: #8b796d;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const WorkLinksRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InlineLinkPill = styled(Anchor)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 6px 12px;
  border: 1px solid rgba(139, 121, 109, 0.45);
  border-radius: 8px;
  color: #6d5b50;
  background: rgba(244, 236, 230, 0.38);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
  &:hover {
    background: rgba(247, 209, 200, 0.28);
    border-color: rgba(139, 121, 109, 0.72);
  }
`;

const MediaSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MediaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const MediaCard = styled.article`
  min-height: 100%;
  padding: 18px 0 22px;
  border-top: 1px solid rgba(59, 69, 90, 0.14);
`;

const MediaMeta = styled.p`
  margin: 0 0 6px;
  color: #8b796d;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const MediaTitle = styled.h3`
  margin: 0;
  color: #2f3545;
  font-size: 15px;
  line-height: 1.4;
`;

export default function PageLanding() {
  return (
    <Page>
      <AboutMeSection>
        <PhotoCard />
        <AboutMeCard />
      </AboutMeSection>
      <WorkSection>
        <SectionHeader>
          <SectionTitle>Work</SectionTitle>
        </SectionHeader>

        <Timeline>
          {workData.map((item) => {
            return (
              <TimelineItem key={item.text}>
                <TimelineHeader>
                  <TimelineTitle>
                    <HighlightSpan>
                      <Anchor target="_blank" href={item.links[0].link} rel="noreferrer">
                        {item.text}
                      </Anchor>
                    </HighlightSpan>
                  </TimelineTitle>
                  <TimelineYear>{item.date}</TimelineYear>
                </TimelineHeader>
                <TimelineMeta>
                  <TimelineRole>{item.role}</TimelineRole> · {item.contribution}
                </TimelineMeta>
                <WorkLinksRow>
                  {item.links.map((workLink) => (
                    <InlineLinkPill
                      key={`${item.text}-${workLink.label}`}
                      target="_blank"
                      href={workLink.link}
                      rel="noreferrer"
                    >
                      {workLink.label} ↗
                    </InlineLinkPill>
                  ))}
                </WorkLinksRow>
              </TimelineItem>
            );
          })}
        </Timeline>

        <MediaSection>
          <SectionTitle>Media</SectionTitle>
          <MediaList>
            {newsData.map((item) => {
              return (
                <MediaCard key={item.text}>
                  <MediaMeta>
                    {item.author} / {item.date}
                  </MediaMeta>
                  <MediaTitle>
                    <HighlightSpan>
                      <Anchor target="_blank" href={item.link} rel="noreferrer">
                        {item.text}
                      </Anchor>
                    </HighlightSpan>
                  </MediaTitle>
                </MediaCard>
              );
            })}
          </MediaList>
        </MediaSection>
      </WorkSection>
    </Page>
  );
}
